import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiDollarSign, FiHeart, FiUsers, FiTrendingUp, FiGift, FiStar } from 'react-icons/fi';
import { getSuggestedDonationAmount } from '../utils/costCalculator';

const Section = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, white 0%, #f8fafc 50%, #f1f5f9 100%);
  &#why-sponsor {
    scroll-margin-top: 80px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  color: var(--primary-blue);
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.3rem;
  color: var(--medium-gray);
  max-width: 800px;
  margin: 0 auto 60px;
  line-height: 1.6;
`;

const BenefitsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
`;

const BenefitCard = styled(motion.div)`
  background: var(--light-gray);
  padding: 40px 30px;
  border-radius: 12px;
  text-align: left;
  border-left: 5px solid var(--primary-orange);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--primary-orange), var(--warm-gold));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: white;
  font-size: 1.5rem;
`;

const BenefitTitle = styled.h4`
  font-size: 1.4rem;
  color: var(--primary-blue);
  margin-bottom: 16px;
`;

const BenefitDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--medium-gray);
`;

const SponsorshipLevels = styled(motion.div)`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #3B82F6 50%, #1E40AF 100%);
  border-radius: 20px;
  padding: 60px 40px;
  color: white;
  text-align: center;
  margin-top: 80px;
  
  * {
    color: white !important;
  }
  
  p {
    color: white !important;
    opacity: 0.9;
  }
`;

const LevelsTitle = styled.h3`
  font-size: 2.5rem;
  margin-bottom: 40px;
  color: white;
`;

const LevelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const LevelCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 30px 20px;
  text-align: center;
`;

const LevelTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 12px;
  color: var(--warm-gold);
`;

const LevelAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 16px;
`;

const LevelDescription = styled.p`
  font-size: 0.9rem;
  opacity: 0.9;
  line-height: 1.5;
`;

const CallToAction = styled(motion.div)`
  text-align: center;
  margin-top: 60px;
  padding: 60px 40px;
  background: var(--light-gray);
  border-radius: 12px;
`;

const CTATitle = styled.h3`
  font-size: 2.2rem;
  color: var(--primary-blue);
  margin-bottom: 20px;
`;

const CTAText = styled.p`
  font-size: 1.2rem;
  color: var(--medium-gray);
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, var(--primary-orange), var(--warm-gold));
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const WhySponsor = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Section id="why-sponsor">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Why Sponsor Ahaar Sewa?
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Your sponsorship creates immediate impact in our community while providing meaningful 
          benefits for your organization and personal fulfillment.
        </SectionSubtitle>

        <BenefitsGrid
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <BenefitCard>
            <BenefitIcon>
              <FiHeart />
            </BenefitIcon>
            <BenefitTitle>Direct Community Impact</BenefitTitle>
            <BenefitDescription>
              Your sponsorship directly feeds 200-400 community members each month. Every dollar 
              goes toward ingredients and supplies, ensuring maximum impact for those facing food insecurity.
            </BenefitDescription>
          </BenefitCard>

          <BenefitCard>
            <BenefitIcon>
              <FiUsers />
            </BenefitIcon>
            <BenefitTitle>Community Partnership</BenefitTitle>
            <BenefitDescription>
              Join a network of local businesses and organizations making a difference. 
              Build lasting relationships while creating meaningful community impact together.
            </BenefitDescription>
          </BenefitCard>

          <BenefitCard>
            <BenefitIcon>
              <FiTrendingUp />
            </BenefitIcon>
            <BenefitTitle>Corporate Social Responsibility</BenefitTitle>
            <BenefitDescription>
              Demonstrate your company's commitment to social impact. We provide documentation 
              and photos to help you share your CSR efforts with stakeholders.
            </BenefitDescription>
          </BenefitCard>

          <BenefitCard>
            <BenefitIcon>
              <FiDollarSign />
            </BenefitIcon>
            <BenefitTitle>Tax-Deductible Donation</BenefitTitle>
            <BenefitDescription>
              Sewa International is a 501(c)(3) organization. Your sponsorship is fully tax-deductible, 
              and we provide all necessary documentation for your records.
            </BenefitDescription>
          </BenefitCard>

          <BenefitCard>
            <BenefitIcon>
              <FiGift />
            </BenefitIcon>
            <BenefitTitle>Recognition & Acknowledgment</BenefitTitle>
            <BenefitDescription>
              Sponsors receive recognition on our website, social media, and at events. We celebrate 
              our partners and ensure your contribution is acknowledged.
            </BenefitDescription>
          </BenefitCard>

          <BenefitCard>
            <BenefitIcon>
              <FiStar />
            </BenefitIcon>
            <BenefitTitle>Transparent Reporting</BenefitTitle>
            <BenefitDescription>
              Receive detailed reports showing exactly how your sponsorship was used, including 
              photos from the event and impact metrics from our community partners.
            </BenefitDescription>
          </BenefitCard>
        </BenefitsGrid>

        <SponsorshipLevels
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <LevelsTitle>Sponsorship Levels</LevelsTitle>
          <p style={{ fontSize: '1.2rem', opacity: '0.9', marginBottom: '40px' }}>
            Choose a sponsorship level that works for you - every contribution makes a difference!
          </p>

          <LevelsGrid>
            <LevelCard>
              <LevelTitle>💝 Custom Donation</LevelTitle>
              <LevelAmount>Any Amount</LevelAmount>
              <LevelDescription>
                Every donation makes a difference! Your contribution will be pooled with others 
                to support our mission. Perfect for any budget - from $25 to $75.
              </LevelDescription>
            </LevelCard>

            <LevelCard>
              <LevelTitle>🤝 Community Supporter</LevelTitle>
              <LevelAmount>${getSuggestedDonationAmount(50)}</LevelAmount>
              <LevelDescription>
                Your contribution is pooled with others to fund upcoming events. Perfect for 
                individuals wanting to make an impact and support our mission.
              </LevelDescription>
            </LevelCard>

            <LevelCard>
              <LevelTitle>💫 Impact Contributor</LevelTitle>
              <LevelAmount>${getSuggestedDonationAmount(100)}</LevelAmount>
              <LevelDescription>
                Your contribution helps fill funding gaps and supports our regular events. 
                Great for small businesses or families honoring special occasions.
              </LevelDescription>
            </LevelCard>

            <LevelCard>
              <LevelTitle>🍽️ Community Champion</LevelTitle>
              <LevelAmount>${getSuggestedDonationAmount(210)}</LevelAmount>
              <LevelDescription>
                Sponsors a complete burrito-making event (210+ burritos). Ideal for companies 
                wanting to make a significant community impact with a dedicated event.
              </LevelDescription>
            </LevelCard>

            <LevelCard>
              <LevelTitle>🏆 Impact Hero</LevelTitle>
              <LevelAmount>${getSuggestedDonationAmount(400)}+</LevelAmount>
              <LevelDescription>
                Sponsors a large-scale community event (400+ burritos). Perfect for corporations 
                seeking maximum community impact and meaningful social responsibility initiatives.
              </LevelDescription>
            </LevelCard>
          </LevelsGrid>
        </SponsorshipLevels>

        <CallToAction
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <CTATitle>Ready to Make a Difference?</CTATitle>
          <CTAText>
            Join our community of sponsors who are helping fight food insecurity in the Bay Area. 
            Every sponsorship creates lasting positive impact.
          </CTAText>
          <CTAButton
            onClick={scrollToContact}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Become a Sponsor Today
          </CTAButton>
        </CallToAction>
      </Container>
    </Section>
  );
};

export default WhySponsor; 